import { PlusCircle } from "@phosphor-icons/react";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuItem,
  DropdownMenuList,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";
import { TEMPLATE_TYPES } from "common/constants";
import useTemplates from "common/datahooks/useTemplates";

import { useCampaignFlowContext } from "../../../context/CampaignFlowContext";
import { useFlowTemplateContext } from "./FlowTemplateContext";
import NewTemplate from "./NewTemplate";

export default function TemplateSelect() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { isFlowError, canEditFlow } = useCampaignFlowContext();

  const {
    flowTemplate: { original_id: selectedTemplateId, type, edited: isEdited },
    isEditing,
    handleSelectTemplate,
  } = useFlowTemplateContext();

  const isDisabled = !canEditFlow || isEditing;

  const { allTemplates, isLoadingAllTemplates } = useTemplates();

  const filteredOptions = allTemplates
    ? allTemplates.filter(
        (option) => option.type === type && option.id !== selectedTemplateId,
      )
    : [];

  const value = allTemplates?.find(({ id }) => id === selectedTemplateId);

  let selectButtonContent = "Choose template";
  if (isLoadingAllTemplates) {
    selectButtonContent = "Loading templates...";
  } else if (value) {
    selectButtonContent = value.name;
    if (isEdited) {
      selectButtonContent = `${value.name} (Edited)`;
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          disabled={isLoadingAllTemplates || isDisabled}
        >
          <SelectButton
            className="flex-1"
            hasError={isFlowError}
            hasSelection={!!value}
            isLoading={isLoadingAllTemplates}
          >
            {selectButtonContent}
          </SelectButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[--radix-popper-anchor-width]">
          <DropdownMenuList>
            {filteredOptions.map((option) => {
              const optionTitle =
                option.id === selectedTemplateId && isEdited
                  ? `${option.name} (Edited)`
                  : option.name;

              const { icon: Icon } = TEMPLATE_TYPES[option.type];

              return (
                <DropdownMenuItem
                  key={option.id}
                  onSelect={() => handleSelectTemplate(option)}
                >
                  <Icon />
                  <div className="flex flex-col gap-0.5">
                    <h5 className="line-clamp-1 text-left">{optionTitle}</h5>
                    <p className="line-clamp-1 break-all text-left text-xs text-black-500">
                      {option.message}
                    </p>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuList>
          <DropdownMenuFooter>
            <DropdownMenuItem
              onSelect={() => {
                setIsCreateDialogOpen(true);
              }}
            >
              <PlusCircle weight="fill" />
              Create new template
            </DropdownMenuItem>
          </DropdownMenuFooter>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewTemplate
        isOpen={isCreateDialogOpen}
        setIsOpen={setIsCreateDialogOpen}
        type={type}
        onCreated={(createdTemplate) => {
          handleSelectTemplate(createdTemplate);
          setIsCreateDialogOpen(false);
        }}
      />
    </>
  );
}
